import React from "react";
import { Icon } from "../../design-system";
import { FORMACION_ACCENTS, fmtMinutes, progressOfCourse } from "./data";

/**
 * Miniatura del curso — gradiente de acento + monograma + chip de categoría.
 * `size`: "sm" | "md" | "lg". `showProgress` pinta la barra inferior.
 */
export function CourseThumb({ course, size = "md", showProgress = false }) {
  const a = FORMACION_ACCENTS[course.accent] || FORMACION_ACCENTS.brand;
  const monogram = course.title.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const pad = size === "lg" ? 24 : size === "sm" ? 12 : 16;
  const fontSize = size === "lg" ? 56 : size === "sm" ? 22 : 36;
  const typeIcon = course.type === "video" ? "Play" : "FileText";
  const progress = progressOfCourse(course);

  return (
    <div
      style={{
        position: "relative", width: "100%", aspectRatio: "16 / 10",
        background: `linear-gradient(135deg, ${a.from} 0%, ${a.to} 100%)`,
        borderRadius: "10px 10px 0 0", overflow: "hidden", color: "#fff",
        display: "flex", flexDirection: "column", justifyContent: "space-between", padding: pad,
      }}
    >
      {/* Rejilla decorativa */}
      <svg width="100%" height="100%" style={{ position: "absolute", inset: 0, opacity: 0.18, pointerEvents: "none" }} aria-hidden="true">
        <defs>
          <pattern id={`grid-${course.id}`} width="22" height="22" patternUnits="userSpaceOnUse">
            <path d="M 22 0 L 0 0 0 22" fill="none" stroke={a.glyph} strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#grid-${course.id})`} />
      </svg>

      <div style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", background: "rgba(0,0,0,0.22)", padding: "3px 8px", borderRadius: 999 }}>
          {course.category}
        </span>
        <Icon name={typeIcon} size={size === "sm" ? 14 : 16} style={{ color: "#fff", opacity: 0.9 }} />
      </div>

      <div style={{ position: "relative", display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontFamily: "var(--kr-font-display)", fontWeight: 700, fontSize, lineHeight: 1, letterSpacing: "-0.04em", color: a.glyph, opacity: 0.95 }}>
          {monogram}
        </span>
        <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: size === "sm" ? 10 : 11, background: "rgba(0,0,0,0.28)", padding: "3px 7px", borderRadius: 4 }}>
          {fmtMinutes(course.durationMin)}
        </span>
      </div>

      {showProgress && progress > 0 && (
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 4, background: "rgba(0,0,0,0.25)" }}>
          <div style={{ height: "100%", width: `${progress * 100}%`, background: "var(--kr-success-500)" }} />
        </div>
      )}
    </div>
  );
}

/** Valoración en estrellas (clip-path) + número mono. */
export function Stars({ value, size = 12 }) {
  const full = Math.floor(value);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          style={{
            width: size, height: size,
            background: i < full ? "var(--kr-warning-500)" : "var(--kr-neutral-200)",
            clipPath: "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)",
            display: "inline-block",
          }}
        />
      ))}
      <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 600, color: "var(--kr-fg-strong)", fontFamily: "var(--kr-font-mono)" }}>{value.toFixed(1)}</span>
    </span>
  );
}

/** Barra de progreso fina con relleno de acento. */
export function ProgressBar({ value, height = 6, accent = "var(--kr-action)", track = "var(--kr-neutral-150)" }) {
  return (
    <div style={{ width: "100%", height, background: track, borderRadius: 999, overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${Math.max(0, Math.min(1, value)) * 100}%`, background: accent, borderRadius: 999, transition: "width .2s var(--kr-ease-out)" }} />
    </div>
  );
}
